/* eslint-disable react-hooks/set-state-in-effect */
/**
 * ProductDetailLayout V2 — Redesign
 *
 * Hero card + sticky tabs + scrollable content.
 * All 8 original tabs preserved.
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Dark/Light Mode via Tailwind Standard
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutGrid, FileText, Images, Radio,
  RefreshCw, CalendarClock, History, Code,
} from 'lucide-react';
import type { Product } from '@/types/products';
import {
  ProductRawDataTab, ProductOverviewTab, ProductChannelsTab,
  ProductHistoryTab, ProductAutomationTab, ProductContentTab,
  ProductAssetsTab, ChannelQuickEditTab,
} from './tabs';
import { Breadcrumbs, Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/v2/ui';
import { ProductHero } from './hero';
import { useProductContentBlocks } from '@/hooks/useContentBlock';
import { startProductSync, getProductImages } from '@/lib/api/products';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { EID } from '@/lib/element-ids.registry';

type TabKey = 'overview' | 'content' | 'assets' | 'channels' | 'quick-edit' | 'automation' | 'history' | 'rawdata';

const TABS: { key: TabKey; label: string; i18nKey?: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'overview', label: 'Übersicht', i18nKey: 'common.overview', icon: LayoutGrid },
  { key: 'content', label: 'Content', icon: FileText },
  { key: 'assets', label: 'Media', icon: Images },
  { key: 'channels', label: 'Channels', icon: Radio },
  { key: 'quick-edit', label: 'Quick Edit', icon: RefreshCw },
  { key: 'automation', label: 'Automatisierung', icon: CalendarClock },
  { key: 'history', label: 'Historie', icon: History },
  { key: 'rawdata', label: 'Raw Data', icon: Code },
];

interface ProductDetailLayoutProps {
  product: Product;
  /** @deprecated — no longer used in redesigned layout, kept for API compat */
  onBack?: () => void;
}

export function ProductDetailLayout({ product }: ProductDetailLayoutProps) {
  const { t } = useTranslation();
  const { user } = useSupabaseAuth();
  const isAdmin = user?.is_admin ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = (searchParams.get('tab') as TabKey) || 'overview';
  const [activeTab, setActiveTab] = useState<TabKey>(tabFromUrl);

  const contentBlocksQuery = useProductContentBlocks(product.id);

  const imagesQuery = useQuery({
    queryKey: ['product-images', product.id],
    queryFn: () => getProductImages(product.id),
  });

  const contentUpdatedAt = contentBlocksQuery.data?.content_blocks
    ?.map((b) => b.updated_at)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  const queryClient = useQueryClient();
  const syncMutation = useMutation({
    mutationFn: () => startProductSync({ provider: 'jtl', full_sync: false, product_id: product.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', product.id] });
      queryClient.invalidateQueries({ queryKey: ['product-content-blocks', product.id] });
    },
  });

   
  useEffect(() => {
    if (activeTab !== tabFromUrl) setActiveTab(tabFromUrl);
  }, [tabFromUrl, activeTab]);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab as TabKey);
    setSearchParams({ tab });
  }, [setSearchParams]);

  const breadcrumbItems = [
    { label: t('products.title'), href: '/v2/products' },
    { label: product.name },
  ];

  return (
    <div className="product-detail-layout min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-screen-2xl mx-auto px-6 pt-4">
        <Breadcrumbs items={breadcrumbItems} />
      </nav>

      {/* Hero */}
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        <ProductHero
          product={product}
          firstImageUrl={product.first_image_url ?? product.image_url ?? imagesQuery.data?.images?.[0]?.thumbnail_url ?? imagesQuery.data?.images?.[0]?.s3_url ?? null}
          imageCount={imagesQuery.data?.images?.length ?? 0}
          isSyncing={syncMutation.isPending}
          onSync={() => syncMutation.mutate()}
          onManageImages={() => handleTabChange('assets')}
        />
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        {/* Sticky Tabs */}
        <div className="sticky top-14 z-40 bg-neutral-50 dark:bg-neutral-950">
          <div className="max-w-screen-2xl mx-auto px-6">
            <TabsList className="w-full justify-start overflow-x-auto border-b border-neutral-200 dark:border-neutral-800">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isLocked = tab.key === 'automation' && !isAdmin;
                return (
                  <TabsTrigger key={tab.key} value={tab.key} disabled={isLocked} id={EID[`product-tab-${tab.key}` as keyof typeof EID]} className="flex items-center gap-2 whitespace-nowrap">
                    <Icon className="h-4 w-4" />
                    {isLocked ? 'Soon' : (tab.i18nKey ? t(tab.i18nKey) : tab.label)}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
        </div>

        {/* Tab Content */}
        <main className="max-w-screen-2xl mx-auto px-6 py-6" aria-label={t('products.title')}>
          <TabsContent value="overview"><ProductOverviewTab product={product} /></TabsContent>
          <TabsContent value="content"><ProductContentTab productId={product.id} /></TabsContent>
          <TabsContent value="assets"><ProductAssetsTab productId={product.id} /></TabsContent>
          <TabsContent value="channels"><ProductChannelsTab productId={product.id} contentUpdatedAt={contentUpdatedAt} /></TabsContent>
          <TabsContent value="quick-edit"><ChannelQuickEditTab productId={product.id} /></TabsContent>
          <TabsContent value="automation"><ProductAutomationTab productId={product.id} /></TabsContent>
          <TabsContent value="history"><ProductHistoryTab productId={product.id} /></TabsContent>
          <TabsContent value="rawdata"><ProductRawDataTab product={product} /></TabsContent>
        </main>
      </Tabs>
    </div>
  );
}
